import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { CinematicButton } from './CinematicButton';
import { LucideIcon } from 'lucide-react';

export interface CinematicEmptyStateProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  variant?: 'default' | 'compact' | 'card';
  className?: string;
}

export const CinematicEmptyState = React.forwardRef<HTMLDivElement, CinematicEmptyStateProps>(
  ({ icon: Icon, title, description, actionLabel, onAction, secondaryActionLabel, onSecondaryAction, variant = 'default', className }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col items-center justify-center text-center',
          variant === 'default' && 'py-16 px-4',
          variant === 'compact' && 'py-8 px-4',
          variant === 'card' && 'p-8 bg-card border border-border rounded-2xl',
          className
        )}
      >
        {/* Icon Container */}
        <div className={cn(
          'flex items-center justify-center rounded-2xl bg-muted mb-4',
          variant === 'default' && 'w-20 h-20',
          variant === 'compact' && 'w-14 h-14',
          variant === 'card' && 'w-16 h-16'
        )}>
          <Icon className={cn(
            'text-muted-foreground',
            variant === 'default' && 'w-10 h-10',
            variant === 'compact' && 'w-7 h-7',
            variant === 'card' && 'w-8 h-8'
          )} />
        </div>

        {/* Title */}
        <h3 className={cn(
          'font-display font-semibold text-foreground',
          variant === 'default' && 'text-xl',
          variant === 'compact' && 'text-lg',
          variant === 'card' && 'text-lg'
        )}>
          {title}
        </h3>

        {/* Description */}
        {description && (
          <p className={cn(
            'text-muted-foreground mt-2 max-w-sm',
            variant === 'default' && 'text-base',
            variant === 'compact' && 'text-sm',
            variant === 'card' && 'text-sm'
          )}>
            {description}
          </p>
        )}

        {/* Actions */}
        {(actionLabel || secondaryActionLabel) && (
          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            {actionLabel && onAction && (
              <CinematicButton
                onClick={onAction}
                variant="primary"
                glow
              >
                {actionLabel}
              </CinematicButton>
            )}
            {secondaryActionLabel && onSecondaryAction && (
              <CinematicButton
                onClick={onSecondaryAction}
                variant="secondary"
              >
                {secondaryActionLabel}
              </CinematicButton>
            )}
          </div>
        )}
      </div>
    );
  }
);

CinematicEmptyState.displayName = 'CinematicEmptyState';

export default CinematicEmptyState;
